import { useEffect, useMemo, useState } from "react";
import { cleanTeamName, matchDivisionLabel } from "@/lib/teamNameClean";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * A block copied from the director's spreadsheet. Column order varies from
 * sheet to sheet, so nothing is assumed: each column is auto-detected from its
 * contents and the mapping is shown for correction before anything is written.
 */
export type PastedTeam = {
  team_name: string;
  division_label: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  expected_amount: number | null;
  /** Populated when the row can't be created as-is. It is shown, never dropped. */
  problem: string | null;
};

/** The fields a pasted column can be mapped onto. */
export const PASTE_FIELDS = [
  { id: "ignore", label: "Ignore" },
  { id: "team_name", label: "Team Name" },
  { id: "division_label", label: "Division" },
  { id: "contact_first", label: "Contact First" },
  { id: "contact_last", label: "Contact Last" },
  { id: "contact_email", label: "Contact Email" },
  { id: "contact_phone", label: "Contact Phone" },
  { id: "expected_amount", label: "Expected Amount" },
] as const;

export type PasteField = (typeof PASTE_FIELDS)[number]["id"];

const money = (v: string) => {
  const n = Number(v.replace(/[$,\s]/g, ""));
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : NaN;
};

const isDivision = (v: string) => /^\d{1,2}\s*u$/i.test(v.trim());
const isEmail = (v: string) => v.includes("@");
const isPhone = (v: string) => {
  const t = v.trim();
  if (!t) return false;
  const digits = t.replace(/\D/g, "");
  return digits.length >= 7 && /^[\d\s().+-]+$/.test(t);
};
const isMoney = (v: string) => {
  const t = v.trim();
  if (!t) return false;
  if (t.startsWith("$")) return !Number.isNaN(money(t));
  return /^\d{1,6}(\.\d{1,2})?$/.test(t) && Number(t) >= 25;
};

/** Tab-separated (Excel/Sheets/Numbers) with a comma-separated fallback. */
export function parsePastedGrid(text: string): string[][] {
  const lines = text.replace(/\r/g, "").split("\n").filter((l) => l.trim() !== "");
  const grid = lines.map((line) =>
    (line.includes("\t") ? line.split("\t") : line.split(",")).map((c) =>
      c.trim().replace(/^"|"$/g, "")
    )
  );
  // A header row pasted along with the data would otherwise become a team.
  if (grid.length > 1 && /^(team|team name|name)$/i.test(grid[0][0] ?? "")) grid.shift();
  return grid;
}

/**
 * Auto-detect what each column holds by looking at its non-empty values.
 * Anything not recognised falls through to team name, then contact first, then
 * contact last — the order a director's sheet usually runs in.
 */
export function detectMapping(grid: string[][]): PasteField[] {
  const width = grid.reduce((w, r) => Math.max(w, r.length), 0);
  const sample = grid.slice(0, 25);
  const mapping: PasteField[] = new Array(width).fill("ignore");
  const unmatched: number[] = [];

  for (let c = 0; c < width; c++) {
    const values = sample.map((r) => r[c] ?? "").filter((v) => v.trim() !== "");
    if (values.length === 0) {
      unmatched.push(c);
      continue;
    }
    const most = (fn: (v: string) => boolean) =>
      values.filter(fn).length / values.length >= 0.6;

    if (most(isDivision)) mapping[c] = "division_label";
    else if (most(isEmail)) mapping[c] = "contact_email";
    else if (most(isPhone)) mapping[c] = "contact_phone";
    else if (most(isMoney)) mapping[c] = "expected_amount";
    else unmatched.push(c);
  }

  const fallbacks: PasteField[] = ["team_name", "contact_first", "contact_last"];
  unmatched.forEach((c, i) => {
    if (i < fallbacks.length) mapping[c] = fallbacks[i];
  });

  // A single column of names is the common case and must always work.
  if (!mapping.includes("team_name") && width > 0) mapping[0] = "team_name";

  return mapping;
}

/**
 * Apply a mapping to the pasted grid, flagging rows rather than dropping them.
 * Team names are cleaned so an age group never lands in the stored name.
 */
export function buildTeams(
  grid: string[][],
  mapping: PasteField[],
  divisionLabels: string[] = []
): PastedTeam[] {
  return grid.map((cells) => {
    const get = (field: PasteField) => {
      const i = mapping.indexOf(field);
      return i === -1 ? "" : (cells[i] ?? "").trim();
    };

    const rawName = get("team_name");
    const cleaned = cleanTeamName(rawName);
    const name = cleaned.name;
    const amountRaw = get("expected_amount");

    let division = get("division_label");
    if (!division && cleaned.ageGroup) {
      division =
        matchDivisionLabel(divisionLabels, cleaned.ageGroup, cleaned.gender) ??
        [cleaned.gender === "girls" ? "Girls" : cleaned.gender === "boys" ? "Boys" : "", cleaned.ageGroup]
          .filter(Boolean)
          .join(" ");
    }

    let problem: string | null = null;
    if (!name) problem = "No team name";

    let expected: number | null = null;
    if (amountRaw) {
      const n = money(amountRaw);
      if (Number.isNaN(n)) problem = problem ?? `"${amountRaw}" isn't an amount`;
      else expected = n;
    }

    const contactName = [get("contact_first"), get("contact_last")]
      .filter(Boolean)
      .join(" ")
      .trim();

    return {
      team_name: name,
      division_label: division,
      contact_name: contactName,
      contact_email: get("contact_email"),
      contact_phone: get("contact_phone"),
      expected_amount: expected,
      problem,
    };
  });
}

type Props = {
  open: boolean;
  /** Raw pasted cells, before mapping. */
  grid: string[][];
  busy?: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (rows: PastedTeam[]) => void;
};

export default function PasteTrackedTeamsDialog({
  open,
  grid,
  busy,
  onOpenChange,
  onConfirm,
}: Props) {
  const [mapping, setMapping] = useState<PasteField[]>([]);

  // Re-detect whenever a new block is pasted.
  useEffect(() => {
    if (open && grid.length > 0) setMapping(detectMapping(grid));
  }, [open, grid]);

  const rows = useMemo(
    () => (mapping.length ? buildTeams(grid, mapping) : []),
    [grid, mapping]
  );
  const valid = rows.filter((r) => !r.problem);
  const flagged = rows.filter((r) => r.problem);
  const preview = rows.slice(0, 5);

  const setColumn = (index: number, field: PasteField) => {
    setMapping((prev) => {
      const next = [...prev];
      // A field can only come from one column — clear the previous holder.
      if (field !== "ignore") {
        for (let i = 0; i < next.length; i++) if (next[i] === field) next[i] = "ignore";
      }
      next[index] = field;
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            Add {valid.length} tracked {valid.length === 1 ? "team" : "teams"}
          </DialogTitle>
          <DialogDescription>
            Each pasted column was matched to a field. Correct anything wrong, then confirm.
            {flagged.length > 0 && (
              <>
                {" "}
                {flagged.length} {flagged.length === 1 ? "row needs" : "rows need"} attention and
                won't be added.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[50vh] overflow-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-muted/80">
              <tr>
                {mapping.map((field, i) => (
                  <th key={i} className="px-2 py-2 text-left font-medium">
                    <Select value={field} onValueChange={(v) => setColumn(i, v as PasteField)}>
                      <SelectTrigger className="h-8 w-[150px] text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PASTE_FIELDS.map((f) => (
                          <SelectItem key={f.id} value={f.id} className="text-xs">
                            {f.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </th>
                ))}
                <th className="px-3 py-2 text-left text-xs font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {grid.slice(0, 5).map((cells, r) => (
                <tr key={r} className={preview[r]?.problem ? "bg-destructive/10" : "border-t"}>
                  {mapping.map((field, c) => (
                    <td
                      key={c}
                      className={
                        field === "ignore"
                          ? "px-2 py-1.5 text-muted-foreground line-through"
                          : "px-2 py-1.5"
                      }
                    >
                      {cells[c] ?? ""}
                    </td>
                  ))}
                  <td className="px-3 py-1.5 text-xs">
                    {preview[r]?.problem ? (
                      <span className="text-destructive">{preview[r]?.problem}</span>
                    ) : (
                      <span className="text-muted-foreground">Ready</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {rows.length > 5 && (
          <p className="text-xs text-muted-foreground">
            Showing the first 5 of {rows.length} pasted rows.
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => onConfirm(valid)} disabled={busy || valid.length === 0}>
            {busy
              ? "Adding…"
              : `Add ${valid.length} tracked ${valid.length === 1 ? "team" : "teams"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
